/**
 * Pricing DSL — locked-down JSON predicate language for `price_rules`.
 *
 * The evaluator (see `./evaluate.ts`) reads only an explicit allowlist of fields
 * on `PricingContext`; unknown field paths short-circuit to `false`. No `eval`,
 * no dynamic property access, no prototype walks. Rules persist as
 * `price_rules.predicate_json` and are also snapshotted into
 * `sale_order_items.price_rule_snapshot_json` so historical sales remain stable
 * even if the live rule is edited or deactivated.
 */

export type PredicateValue = string | number | boolean | null;

export type PredicateLeaf =
  | { op: 'eq' | 'neq'; field: string; value: PredicateValue }
  | { op: 'in' | 'nin'; field: string; values: ReadonlyArray<string | number> }
  | { op: 'between'; field: string; min: number; max: number; inclusive?: 'both' | 'min' | 'max' | 'none' }
  | { op: 'gte' | 'lte' | 'gt' | 'lt'; field: string; value: number };

export type PredicateBranch =
  | { op: 'and' | 'or'; clauses: ReadonlyArray<Predicate> }
  | { op: 'not'; clause: Predicate };

export type Predicate = PredicateLeaf | PredicateBranch;

export type WeightSource = 'pos' | 'manual' | 'scale' | 'consultation';

export interface PricingContext {
  readonly pet?: {
    readonly species?: string | null;
    readonly sex?: 'male' | 'female' | 'unknown' | null;
    readonly breed?: string | null;
    /** Current measured weight captured for THIS visit/sale. Never derived from `pets.weight_grams`. */
    readonly current_weight_grams?: number | null;
    readonly approximate_age_months?: number | null;
  };
  readonly customer?: {
    readonly id?: string | null;
    readonly category?: string | null;
  };
  readonly branch_id?: string | null;
  readonly date?: string;
}

/**
 * Allowlist of dot-paths the predicate evaluator will read off the context.
 * Any other field path returns `undefined` (and the predicate evaluates to false).
 */
export const PRICING_ALLOWED_FIELDS = [
  'pet.species',
  'pet.sex',
  'pet.breed',
  'pet.current_weight_grams',
  'pet.approximate_age_months',
  'customer.id',
  'customer.category',
  'branch_id',
  'date',
] as const;

export type PricingAllowedField = (typeof PRICING_ALLOWED_FIELDS)[number];

/** Weight-keyed fields — drive the `requires_current_weight` inference for rules. */
export const WEIGHT_KEYED_FIELDS: ReadonlyArray<PricingAllowedField> = ['pet.current_weight_grams'];
